/* @flow */
import * as React from "react";
import styled, { css } from "styled-components";
import Tooltip from "./Tooltip/NextGenTooltip";
import Layers from "./Layers";
import * as Types from "./NextGenChartTypes";
import type { Deal as DealObject } from "../../redux/CommonTypes";
import Money from "../Money";
import { withFetch } from "../common/fetch";
import { tournamentCurrency } from "../Icons/Kuchka";
import { rgba } from "polished";
import { fontSize } from "../common/styled-mixins";
import Gettext from "../common/Gettext";
import { NewSimpleSpinner } from "../Spinners/SimpleSpinner";
import Mobile from "../layout/Mobile";
import Desktop from "../layout/Desktop";
import Close from "../Icons/Close";

// СЛОВАРЬ
// positive/negative: Ставка на удорожание или удешевление стока
// profitable: Сбывается ли предсказание в данный момент

export const dealDuplicatesReducer = (result, deal, index, deals) => {
  if (result.length > 0) {
    const last = result[result.length - 1];

    if (
      deal.timestamp === last.timestamp &&
      deal.bidType === last.bidType &&
      deal.amount === last.amount
    ) {
      last.multiplier += 1;
      let duplicates = last.duplicates || [];
      duplicates.push(deal);
      last.duplicates = duplicates;
    } else {
      result.push({ ...deal, multiplier: 1 });
    }
  } else {
    result.push({ ...deal, multiplier: 1 });
  }

  return result;
};

const BarView = styled.div`
  position: absolute;
  top: 0px;
  left: 0px;
  height: 1px;
  background-color: ${props =>
    props.isPositive ? "rgb(96,163,28)" : "rgb(230,76,60)"};

  ${props =>
    props.vertical &&
    css`
      top: 23px;
      left: 1px;
      width: 1px;
      height: calc(100% - 65px);
    `} ${props =>
    props.mobile &&
    css`
      background-color: ${props =>
        props.isPositive ? "rgba(96,163,28,.75)" : "rgba(230,76,60,.75)"};
    `};

  ${props =>
    props.vertical &&
    props.mobile &&
    css`
      bottom: 0;
      height: calc(100% - 23px);
    `};
`;

const RightIndicatorView = styled.svg`
  position: absolute;
  top: -8px;
  left: -8px;
  width: 16px;
  height: 17px;
  ${"" /* border-radius: 8px; */} z-index: ${Layers.INDICATOR};
  text-align: center;
  -webkit-backface-visibility: hidden;

  ${props =>
    props.mobile &&
    css`
      top: -6px;
      left: -6px;
      width: 12px;
      height: 13px;
    `};
`;

const RightIndicatorBg = styled.path`
  fill-rule: evenodd;
  fill: ${props => props.theme.main};
`;

const RightIndicatorBorder = styled.path`
  fill-rule: evenodd;
  fill: ${props => (props.isPositive ? "rgb(96,163,28)" : "rgb(230,76,60)")};
`;

const RightIndicatorIcon = styled.path`
  fill-rule: evenodd;
  fill: ${props => props.theme.accent};
`;

export const LeftIndicatorView = styled.div`
  position: absolute;
  top: -5px;
  left: -4px;
  width: 9px;
  height: 9px;
  background-color: ${props => props.theme.main};
  background-clip: padding-box;
  border: 1px solid ${({ theme }) => theme.foreground};
  border-radius: 50%;
  z-index: ${Layers.TOOLTIP};

  ${props =>
    props.mobile &&
    css`
      top: -3px;
      left: -2px;
      width: 5px;
      height: 5px;
    `};
`;

const SellDealBlock = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 85px;
  padding: 14px 5px;
  background-color: ${({ theme }) => rgba(theme.sellDealBg, 0.9)};
  border: 1px solid ${({ theme }) => theme.sellDealBorder};

  @media (min-width: 960px) {
    min-width: 130px;
  }

  ${({ isPositive, isRevert }) =>
    isPositive
      ? isRevert
        ? css`
            bottom: calc(100% + 2px);
            border-radius: 4px;
          `
        : css`
            top: calc(100% + 2px);
            border-radius: 4px;
          `
      : isRevert
      ? css`
          top: calc(100% + 2px);
          border-radius: 4px;
        `
      : css`
          bottom: calc(100% + 2px);
          border-radius: 4px;
        `};
`;

const SellDealProfit = styled.span`
  display: block;
  margin-bottom: 7px;
  font-size: ${fontSize(12)};
  font-weight: 700;
  color: ${({ theme }) => theme.sellDealProfitColor};
  text-align: center;
`;

const SellDealButton = styled.span`
  display: block;
  font-size: ${fontSize(12)};
  color: ${({ theme }) => theme.sellDealButtonColor};
  text-decoration: underline;
  text-align: center;
  cursor: pointer;

  &:hover {
    text-decoration: none;
  }
`;

const SellDeal = ({
  isPositive,
  profit,
  onClick,
  y,
  chartHeight,
  ...props
}) => {
  const isRevert = isPositive ? chartHeight - y - 70 < 0 : y - 70 < 0;

  return (
    <SellDealBlock isPositive={isPositive} isRevert={isRevert}>
      <SellDealProfit>
        <Money>{props.multiplier * profit}</Money>
      </SellDealProfit>
      {props.refundFetching.has(props.dealId) ? (
        <NewSimpleSpinner />
      ) : (
        <SellDealButton
          onClick={onClick}
          onTouchStart={e => {
            e.target.refundClick = true;
            onClick(e);
          }}
        >
          {props.multiplier > 1 ? (
            <Gettext>Close {props.multiplier} deals</Gettext>
          ) : (
            <Gettext>Close the deal</Gettext>
          )}
        </SellDealButton>
      )}
    </SellDealBlock>
  );
};

function HorizontalBar(props) {
  return (
    <BarView
      style={{
        width: `${props.x + props.width}px`,
        WebkitTransform: `translate3d(0, ${props.y}px, 0)`,
        transform: `translate3d(0, ${props.y}px, 0)`
      }}
      mobile={props.mobile}
      isPositive={props.isPositive}
    />
  );
}

function VerticalBar(props) {
  return (
    <BarView
      vertical
      style={{
        WebkitTransform: `translateX(${props.x + props.width}px)`,
        transform: `translateX(${props.x + props.width}px)`
      }}
      mobile={props.mobile}
      isPositive={props.isPositive}
    />
  );
}

function RightIndicator(props) {
  return (
    <RightIndicatorView
      style={{
        WebkitTransform: `translate3d(${props.x}px, ${
          props.y
        }px, 0) scale(1.0, 1.0)`,
        transform: `translate3d(${props.x}px, ${props.y}px, 0) scale(1.0, 1.0)`,
        borderColor: props.isPositive ? "rgb(96,163,28)" : "rgb(230,76,60)"
      }}
      width="16px"
      height="17px"
      viewBox="0 0 16 17"
      mobile={props.mobile}
    >
      <RightIndicatorBg d="M8.529,1.738 C12.260,1.738 15.284,4.763 15.284,8.493 C15.284,12.224 12.260,15.249 8.529,15.249 C4.798,15.249 1.774,12.224 1.774,8.493 C1.774,4.763 4.798,1.738 8.529,1.738 Z" />
      <RightIndicatorBorder
        isPositive={props.isPositive}
        d="M16.008,8.501 C16.008,12.655 12.641,16.021 8.488,16.021 C4.335,16.021 0.968,12.655 0.968,8.501 C0.968,4.348 4.335,0.981 8.488,0.981 C12.641,0.981 16.008,4.348 16.008,8.501 ZM8.483,2.143 C4.971,2.143 2.124,4.990 2.124,8.501 C2.124,12.013 4.971,14.860 8.483,14.860 C11.994,14.860 14.841,12.013 14.841,8.501 C14.841,4.990 11.994,2.143 8.483,2.143 Z"
      />
      {props.isProfitable !== null &&
        (props.isProfitable ? (
          <RightIndicatorIcon d="M12.000,9.000 L9.000,9.000 L9.000,12.000 L8.000,12.000 L8.000,9.000 L5.000,9.000 L5.000,8.000 L8.000,8.000 L8.000,5.000 L9.000,5.000 L9.000,8.000 L12.000,8.000 L12.000,9.000 Z" />
        ) : (
          <RightIndicatorIcon d="M5.732,9.032 L5.732,8.032 L11.290,8.032 L11.290,9.032 L5.732,9.032 Z" />
        ))}
    </RightIndicatorView>
  );
}

const Currency = styled(Money.Symbol)`
  font-size: 0.75em;
`;

export const dealCloser = (deal, closeFn) => {
  if (deal.id) {
    closeFn(deal.id);
  }

  if (deal.duplicates) {
    deal.duplicates.forEach(duplicate => dealCloser(duplicate, closeFn));
  }
};

function LeftIndicator({ tooltip = true, ...props }) {
  return (
    <LeftIndicatorView
      style={{
        WebkitTransform: `translate3d(${props.x}px, ${props.y}px, 0)`,
        transform: `translate3d(${props.x}px, ${props.y}px, 0)`,
        backgroundColor: props.isPositive ? "rgb(96,163,28)" : "rgb(230,76,60)"
      }}
      mobile={props.mobile}
    >
      {tooltip && (
        <Tooltip mobile={props.mobile} isPositive={props.isPositive}>
          {props.multiplier > 1 && `${props.multiplier} ✕\u00A0`}
          <Money
            Symbol={Currency}
            currency={
              props.tradeMode === "tournament" ? tournamentCurrency : null
            }
          >
            {props.amount}
          </Money>

          {props.isRefundable && !props.isDealMid && (
            <Mobile>
              <Close
                width={10}
                height={10}
                strokeWidth={3}
                style={{ marginLeft: 5, marginTop: 2, opacity: 0.6 }}
                onClick={props.refundClickHandler}
                onTouchStart={e => {
                  e.target.refundClick = true;
                  props.refundClickHandler(e);
                }}
              />
            </Mobile>
          )}
        </Tooltip>
      )}

      {props.isRefundable && !props.isDealMid && (
        <Desktop>
          <SellDeal
            multiplier={props.multiplier}
            dealId={props.dealId}
            profit={props.refundProfit}
            onClick={props.refundClickHandler}
            refundFetching={props.refundFetching}
            isPositive={props.isPositive}
            y={props.y}
            chartHeight={props.chartHeight}
          />
        </Desktop>
      )}
    </LeftIndicatorView>
  );
}

export function getRefundPercent(
  returnK: number,
  bidType: boolean,
  refundPoints: Object
) {
  let refundPercent;

  if (bidType) {
    if (returnK < refundPoints.refund_10) {
      refundPercent = 0;
    } else if (
      returnK >= refundPoints.refund_10 &&
      returnK < refundPoints.refund_30
    ) {
      refundPercent = 0.1;
    } else if (
      returnK >= refundPoints.refund_30 &&
      returnK < refundPoints.refund_95
    ) {
      refundPercent = 0.3;
    } else if (
      returnK >= refundPoints.refund_95 &&
      returnK < refundPoints.refund_100
    ) {
      refundPercent = 0.95;
    } else if (
      returnK >= refundPoints.refund_100 &&
      returnK < refundPoints.refund_105
    ) {
      refundPercent = 1;
    } else if (
      returnK >= refundPoints.refund_105 &&
      returnK < refundPoints.refund_110
    ) {
      refundPercent = 1.05;
    } else if (
      returnK >= refundPoints.refund_110 &&
      returnK < refundPoints.refund_115
    ) {
      refundPercent = 1.1;
    } else {
      refundPercent = 1.15;
    }
  } else {
    if (returnK <= refundPoints.refund_115) {
      refundPercent = 1.15;
    } else if (
      returnK > refundPoints.refund_115 &&
      returnK <= refundPoints.refund_110
    ) {
      refundPercent = 1.1;
    } else if (
      returnK > refundPoints.refund_110 &&
      returnK <= refundPoints.refund_105
    ) {
      refundPercent = 1.05;
    } else if (
      returnK > refundPoints.refund_105 &&
      returnK <= refundPoints.refund_100
    ) {
      refundPercent = 1;
    } else if (
      returnK > refundPoints.refund_100 &&
      returnK <= refundPoints.refund_95
    ) {
      refundPercent = 0.95;
    } else if (
      returnK > refundPoints.refund_95 &&
      returnK <= refundPoints.refund_30
    ) {
      refundPercent = 0.3;
    } else if (
      returnK > refundPoints.refund_30 &&
      returnK <= refundPoints.refund_10
    ) {
      refundPercent = 0.1;
    } else {
      refundPercent = 0;
    }
  }

  return refundPercent;
}

type Props = {
  ...DealObject,
  tooltip?: boolean,
  tradeMode: boolean,
  scaleX: (date: number) => number,
  scaleY: (date: number) => number,
  lastPoint: Types.RenderablePoint,
  opacity: number,
  multiplier: number,
  mobile?: boolean,
  viewportWidth: number
};

const Deal = (props: Props) => {
  const start = props.scaleX(props.open_timestamp * 1000);
  const end = props.scaleX((props.open_timestamp + props.bTime) * 1000);
  const dealMid = (props.open_timestamp + props.bTime / 2) * 1000;

  const isDealMid = dealMid < props.now;

  // const features = api.readEntity("/api/features/list");
  // const closeEarly = features.value && features.value.find(feature => feature.name === "operation_early_close");
  const isRefundable =
    props.refund_10 != null &&
    props.refund_30 != null &&
    props.refund_95 != null &&
    props.refund_100 != null &&
    props.refund_105 != null &&
    props.refund_110 != null &&
    props.refund_115 != null;

  let refundProfit;
  if (isRefundable) {
    const returnK = (props.lastPoint[2].value - props.value) / props.value;
    const refundPercent = getRefundPercent(returnK, props.bidType, {
      refund_10: props.refund_10,
      refund_30: props.refund_30,
      refund_95: props.refund_95,
      refund_100: props.refund_100,
      refund_105: props.refund_105,
      refund_110: props.refund_110,
      refund_115: props.refund_115
    });
    refundProfit = props.amount * refundPercent;
  }

  const valueY = props.scaleY(props.value);

  const endsOffscreen = end > props.viewportWidth;

  const width = endsOffscreen
    ? Math.floor(props.viewportWidth - start)
    : Math.floor(end - start);

  let isProfitable = null;

  if (props.lastPoint.value !== props.value) {
    isProfitable = props.bidType
      ? props.lastPoint[2].value >= props.value
      : props.lastPoint[2].value <= props.value;
  }

  const dealIsActive = props.profit === undefined;
  const shouldShowTimer = !endsOffscreen && dealIsActive;

  return (
    <React.Fragment>
      <HorizontalBar
        x={start}
        y={valueY}
        width={width}
        isPositive={props.bidType}
        mobile={props.mobile}
      />
      {!endsOffscreen && (
        <VerticalBar
          x={start}
          y={valueY}
          width={width}
          isPositive={props.bidType}
          mobile={props.mobile}
        />
      )}
      <LeftIndicator
        x={start}
        y={valueY}
        dealId={props.id}
        tooltip={props.tooltip}
        isPositive={props.bidType}
        amount={props.amount}
        mobile={props.mobile}
        multiplier={props.multiplier}
        tradeMode={props.tradeMode}
        isDealMid={isDealMid}
        isRefundable={isRefundable}
        refundProfit={refundProfit}
        chartHeight={props.chartHeight}
        refundClickHandler={() => dealCloser(props, props.refundClickHandler)}
        refundFetching={props.refundFetching}
      />
      {shouldShowTimer && (
        <RightIndicator
          x={end}
          y={valueY}
          isPositive={props.bidType}
          isProfitable={isProfitable}
          mobile={props.mobile}
        />
      )}
    </React.Fragment>
  );
};

export default withFetch(Deal);



// WEBPACK FOOTER //
// ./src/components/Chart/Deal.js